import React, { useMemo, useState, useRef, useEffect } from "react";
import {
  // Added TextField for the search input
  Card,
  CardActionArea,
  CardMedia,
  CardContent,
  Typography,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Container,
  Box,
  Button,
  Pagination,
  TextField, // <--- New Import for Search Bar
  InputAdornment, // <--- New Import for Search Icon
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import SearchIcon from "@mui/icons-material/Search"; // <--- New Import for Search Icon
import CruiseBanner from "./CruiseImages/CruiseBanner.png";

// Assuming this path to your data file
import rawCruiseData from "./cruiseData"; // Renamed import to avoid conflict
import "./cruise.css";

// Assuming a default WhatsApp number
const WHATSAPP_NUMBER = "917337864840";
const ITEMS_PER_PAGE = 10; // Set to 10 cards per page

// Fallback constant for missing images
const FALLBACK_PLACEHOLDER =
  "https://placehold.co/800x500/cccccc/333333?text=Image+Unavailable";

// -------------------------------------------------------------------
// NEW: Custom Hook to ensure data stability and unique IDs
// -------------------------------------------------------------------
const useCruiseDestinationsData = () => {
  const dataWithStableId = useMemo(() => {
    // This runs once when the component mounts and ensures every destination
    // has a guaranteed unique internal ID for React keys.
    return rawCruiseData.map((dest, index) => ({
      ...dest,
      // Create a stable internal ID based on the original index,
      // which is guaranteed to be unique if the data source is constant.
      internalId: `cruise-dest-${index}`,
    }));
  }, []); // Empty dependency array ensures it only runs once

  return dataWithStableId;
};

// -------------------------------------------------------------------
// NEW: Image Gallery Component
// -------------------------------------------------------------------

const ImageGallery = ({ images, title }) => {
  // Filter out any potentially null/undefined/empty string images
  const validImages = images.filter((img) => img);

  // Use the first valid image as the initial main image
  const [mainImage, setMainImage] = useState(
    validImages[0] || FALLBACK_PLACEHOLDER
  );

  // Re-sync the main image if the destination changes
  useEffect(() => {
    setMainImage(validImages[0] || FALLBACK_PLACEHOLDER);
  }, [images]);

  if (validImages.length === 0) {
    return (
      <div className="detail-image-wrapper">
        <img
          src={FALLBACK_PLACEHOLDER}
          alt={`Image for ${title}`}
          className="detail-hero-image"
        />
      </div>
    );
  }

  return (
    <div className="detail-image-gallery">
      {/* Main Display Image */}
      <div className="detail-main-image-wrapper">
        <img
          src={mainImage}
          alt={`Main image of ${title}`}
          className="detail-hero-image"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = FALLBACK_PLACEHOLDER;
          }}
        />
      </div>

      {/* Thumbnails */}
      {validImages.length > 1 && (
        <div className="detail-thumbnails-container">
          {validImages.map((imgUrl, index) => (
            <img
              key={index}
              src={imgUrl}
              alt={`Thumbnail ${index + 1} of ${title}`}
              className={`detail-thumbnail-image ${
                imgUrl === mainImage ? "active" : ""
              }`}
              onClick={() => setMainImage(imgUrl)}
              onError={(e) => {
                e.target.onerror = null;
                // Use a smaller placeholder for broken thumbnails
                e.target.src =
                  "https://placehold.co/100x70/bbbbbb/333333?text=N/A";
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// -------------------------------------------------------------------
// UPDATED: CruiseBanner Component
// -------------------------------------------------------------------

const CruiseBannerComponent = () => {
  // Using a vibrant image placeholder of popular cruise ships/destinations

  return (
    <div className="hero-banner-container">
      <img
        src={CruiseBanner}
        alt="Cruise Destinations Banner - Cruise Ships and Sea"
        className="hero-banner-image"
        onError={(e) => {
          e.target.onerror = null;
          e.target.src =
            "https://placehold.co/1920x450/004a80/ffffff?text=Discover+Your+Next+Cruise";
        }}
      />
      <div className="hero-content-overlay">
        <h1 className="hero-title">Cruise Destinations</h1>
        <p className="hero-subtitle">
          Explore curated cruise packages and find your dream voyage.
        </p>
      </div>
    </div>
  );
};

// -------------------------------------------------------------------
// 1. DestinationDetailView Component (Full-Page Detail)
// -------------------------------------------------------------------

const DestinationDetailView = ({ dest, setSelectedDestination }) => {
  if (!dest) {
    return (
      <Typography variant="h5" align="center" sx={{ mt: 5 }}>
        Destination not found
      </Typography>
    );
  }

  // Determine the list of images for the gallery.
  const detailImages =
    Array.isArray(dest.images) && dest.images.length > 0
      ? dest.images
      : dest.image
      ? [dest.image]
      : [];

  const currentDest = {
    ...dest,
    // Use the determined image list
    images: detailImages,
    // Ensure these properties exist, otherwise they will be undefined/null
    overview: Array.isArray(dest.overview)
      ? dest.overview
      : dest.overview
      ? [dest.overview]
      : [],
    itinerary: dest.itinerary || [],
    inclusions: dest.inclusions || [],
    exclusions: dest.exclusions || [],
  };

  // State for Accordions (Itinerary)
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [expandedDays, setExpandedDays] = useState({});

  const handleDayAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedDays((prevState) => ({
      ...prevState,
      [panel]: isExpanded,
    }));
  };

  const handleExpandAll = () => {
    const allExpanded = currentDest.itinerary.reduce((acc, dayPlan) => {
      acc[`day-${dayPlan.day}`] = true;
      return acc;
    }, {});
    setExpandedDays(allExpanded);
  };

  const handleCollapseAll = () => {
    setExpandedDays({});
  };

  const whatsappLink = `https://wa.me/${WHATSAPP_NUMBER}?text=I%20am%20interested%20in%20the%20${encodeURIComponent(
    currentDest.title
  )}%20cruise%20package%20(${encodeURIComponent(
    currentDest.duration || "N/A"
  )}).%20Please%20send%20me%20booking%20details.`;

  // 1. Overview Accordion Component
  const overviewAccordion = (
    <Accordion
      defaultExpanded
      className="detail-itinerary-accordion detail-overview-accordion"
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h6" className="accordion-main-title">
          Overview
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        {currentDest.overview?.length > 0 ? (
          <ul className="detail-info-list">
            {currentDest.overview.map((item, index) => (
              <li key={`ov-${index}`}>{item}</li>
            ))}
          </ul>
        ) : (
          <Typography>Overview details are not available yet.</Typography>
        )}
      </AccordionDetails>
    </Accordion>
  );

  // 2. Inclusions Accordion Component
  const inclusionsAccordion = (
    <Accordion
      defaultExpanded
      className="detail-itinerary-accordion detail-inclusions-accordion"
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h6" className="accordion-main-title">
          Inclusions
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        {currentDest.inclusions?.length > 0 ? (
          <ul className="inclusion-custom-list">
            {currentDest.inclusions.map((item, index) => (
              <li key={`inc-${index}`}>{item}</li>
            ))}
          </ul>
        ) : (
          <Typography>Inclusions list is not available yet.</Typography>
        )}
      </AccordionDetails>
    </Accordion>
  );

  // 3. Exclusions Accordion Component
  const exclusionsAccordion = (
    <Accordion
      defaultExpanded
      className="detail-itinerary-accordion detail-exclusions-accordion"
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h6" className="accordion-main-title">
          Exclusions
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        {currentDest.exclusions?.length > 0 ? (
          <ul className="exclusion-custom-list">
            {currentDest.exclusions.map((item, index) => (
              <li key={`exc-${index}`}>{item}</li>
            ))}
          </ul>
        ) : (
          <Typography>Exclusions list is not available yet.</Typography>
        )}
      </AccordionDetails>
    </Accordion>
  );

  return (
    // Applied the wrapper class
    <div className="cruise-page-wrapper">
      <div className="destination-detail-page">
        {/* Detail Header Bar */}
        <div className="detail-navigation-header">
          <button
            className="back-to-list-button"
            onClick={() => setSelectedDestination(null)} // Go back to the list
          >
            &larr; Back to Cruises
          </button>
        </div>

        {/* Main Content Area */}
        <div className="detail-main-content">
          {/* LEFT COLUMN */}
          <div className="detail-primary-column">
            {/* Image Gallery */}
            <ImageGallery
              images={currentDest.images}
              title={currentDest.title}
            />

            {/* Title and Duration Tag moved here, just below the image */}
            <div className="title-and-duration-info">
              <h2>{currentDest.title}</h2>
              <span className="duration-label">
                <CalendarMonthIcon className="duration-icon" />
                {currentDest.duration || "Duration N/A"}
              </span>
            </div>
            {/* Subtitle section (margin adjustment targets the p tag inside subtitle-info) */}
            <div className="subtitle-info">
              <p>{currentDest.subtitle}</p>
            </div>

            {/* Overview for Mobile/Tablet */}
            <Box className="overview-mobile-container">{overviewAccordion}</Box>

            {/* Detailed Itinerary Accordion */}
            <Accordion defaultExpanded className="detail-itinerary-accordion">
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6" className="accordion-main-title">
                  Detailed Itinerary
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {/* Expand/Collapse buttons for Detailed Itinerary */}
                <Box className="itinerary-action-controls">
                  <Button
                    onClick={handleExpandAll}
                    variant="outlined"
                    size="small"
                    className="itinerary-toggle-button"
                  >
                    Expand All
                  </Button>
                  <Button
                    onClick={handleCollapseAll}
                    variant="outlined"
                    size="small"
                    className="itinerary-toggle-button"
                  >
                    Collapse All
                  </Button>
                </Box>

                {currentDest.itinerary?.length > 0 ? (
                  // Itinerary uses nested accordions
                  currentDest.itinerary.map((dayPlan, index) => (
                    <Accordion
                      key={index}
                      disableGutters
                      className="day-plan-accordion"
                      expanded={expandedDays[`day-${dayPlan.day}`] || false}
                      onChange={handleDayAccordionChange(`day-${dayPlan.day}`)}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle1" component="div">
                          <span className="day-title-emphasis">
                            Day {dayPlan.day}:
                          </span>{" "}
                          {dayPlan.title}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        {/* Day Details content in ul li format */}
                        <ul className="day-detail-list">
                          {Array.isArray(dayPlan.details) ? (
                            dayPlan.details.map((detail, idx) => (
                              <li key={idx}>{detail}</li>
                            ))
                          ) : (
                            <li>{dayPlan.details}</li>
                          )}
                        </ul>
                      </AccordionDetails>
                    </Accordion>
                  ))
                ) : (
                  <Typography>
                    Itinerary details are not available yet.
                  </Typography>
                )}
              </AccordionDetails>
            </Accordion>

            {/* Inclusions Accordion */}
            {inclusionsAccordion}

            {/* Exclusions Accordion */}
            {exclusionsAccordion}
          </div>

          {/* RIGHT COLUMN */}
          <Box className="detail-secondary-column">
            {/* Overview for Desktop */}
            <Box className="overview-desktop-container">
              {overviewAccordion}
            </Box>

            {/* Booking/Contact Section */}

            <Box className="contact-action-box">
              <p className="ready-book">Ready to Book?</p>
              <p className="contact-customize">
                Contact us now to customize your trip or to book this package.
              </p>
              <a
                href={whatsappLink}
                target="_blank"
                rel="noreferrer"
                className="whatsapp-contact-button"
              >
                <WhatsAppIcon />
                Chat Us on WhatsApp
              </a>
            </Box>
          </Box>
        </div>
      </div>
    </div>
  );
};

// -------------------------------------------------------------------
// 2. Main Cruise Component (List View & Logic)
// -------------------------------------------------------------------

const Cruise = () => {
  // Use the custom hook to get the stable, internally-ID'd data
  const allDestinations = useCruiseDestinationsData();

  const [selectedDestination, setSelectedDestination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState(""); // <--- New State for Search

  // 2. Filtering Logic (Uses useMemo for performance)
  const filteredDestinations = useMemo(() => {
    if (!searchTerm) {
      return allDestinations;
    }

    const lowerCaseSearch = searchTerm.toLowerCase();

    return allDestinations.filter(
      (dest) =>
        dest.title.toLowerCase().includes(lowerCaseSearch) ||
        (dest.duration && dest.duration.toLowerCase().includes(lowerCaseSearch))
    );
  }, [searchTerm, allDestinations]); // Dependencies: recalculate only when search term or data changes

  // Reset page to 1 whenever the search term changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // 3. Pagination Calculations
  const totalPages = Math.ceil(filteredDestinations.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentDestinations = filteredDestinations.slice(startIndex, endIndex);

  // 4. Event Handlers
  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    // Scroll to the top of the destination list area when changing pages
    const container = document.getElementById("destination-list-top-anchor");
    if (container) {
      requestAnimationFrame(() => {
        container.scrollIntoView({ behavior: "smooth" });
      });
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  if (selectedDestination) {
    return (
      <DestinationDetailView
        dest={selectedDestination}
        setSelectedDestination={setSelectedDestination}
      />
    );
  }

  // Otherwise, render the main list view
  return (
    // Applied the wrapper class
    <div className="cruise-page-wrapper">
      {/* NEW: Hero Banner at the top of the page */}
      <CruiseBannerComponent />

      <Container className="cruise-list-container">
        {/* Centering the heading */}

        {/* Search Bar - Professional Look */}
        <Box sx={{ mb: 4, display: "flex", justifyContent: "center" }}>
          <TextField
            label="Search Cruise"
            variant="outlined"
            value={searchTerm}
            onChange={handleSearchChange}
            fullWidth={false} // Allow it to be centered and not full width
            sx={{ maxWidth: "600px", width: "100%" }} // Adjust width for professional look
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        {/* End Search Bar */}

        {/* Container for the List and Pagination Top (used for scrolling) */}
        <div id="destination-list-top-anchor">
          {/* Card Grid */}
          <Grid
            className="destination-card-grid"
            container
            spacing={4}
            justifyContent="center"
          >
            {currentDestinations.length > 0 ? (
              // FIX APPLIED HERE: Using the guaranteed unique internalId
              currentDestinations.map((dest) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={4}
                  // lg={3} // Adjusted for 4 columns on large screens
                  key={dest.internalId} // <<<--- USE THE GUARANTEED UNIQUE ID
                  className="destination-card-item"
                >
                  <Card className="destination-summary-card">
                    {/* On click, set the destination to state to render the detail view (full page) */}
                    <CardActionArea
                      onClick={() => setSelectedDestination(dest)}
                      className="card-click-area"
                    >
                      {/* CARD LIST VIEW: Use dest.image (single string) */}
                      <CardMedia
                        component="img"
                        height="200"
                        image={dest.image || FALLBACK_PLACEHOLDER}
                        alt={dest.title}
                      />
                      <CardContent className="card-info-content">
                        <Typography variant="h6">{dest.title}</Typography>
                        <div className="card-icon">
                          <CalendarMonthIcon className="duration-icon-list" />
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            className="duration"
                          >
                            {dest.duration || "Details available on click"}
                          </Typography>
                        </div>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Typography
                  variant="h6"
                  align="center"
                  className="empty-results-message"
                >
                  No cruises available. {searchTerm && `for "${searchTerm}"`}
                </Typography>
              </Grid>
            )}
          </Grid>
        </div>

        {/* Pagination Component */}
        {totalPages > 1 && (
          <Box className="pagination-controls-container list-pagination-bottom">
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              size="large"
              showFirstButton
              showLastButton
              className="styled-pagination"
            />
          </Box>
        )}
      </Container>
    </div>
  );
};

export default Cruise;
